import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import styled from "@emotion/styled";
import { keyframes, css } from "@emotion/react";
import { useHistory } from "react-router-dom";
import { Header } from "../../components/layout/header/header";
import { GET_USER_EDIT } from "../../apollo-client/queries";
import avatar1 from "../../img/user-avatars/avatar1.svg";
import avatar2 from "../../img/user-avatars/Avatar2.svg";
import avatar3 from "../../img/user-avatars/Avatar3.svg";
import avatar4 from "../../img/user-avatars/Avatar4.svg";
import avatar5 from "../../img/user-avatars/Avatar5.svg";
import avatar6 from "../../img/user-avatars/Avatar6.svg";
import avatar7 from "../../img/user-avatars/Avatar7.svg";
import avatar8 from "../../img/user-avatars/Avatar8.svg";
import avatar9 from "../../img/user-avatars/Avatar9.svg";

import { EDIT_USER_NAME, EDIT_USER_PHOTO } from "../../apollo-client/mutations";
import { ProfilePhotoWithName } from "./profile-photo-with-name";
import { showErrorToast, showSuccessToast } from "../../toast/toast-service";
import { FormattedMessage } from "react-intl";

export const AvatarsPage = (props) => {
  //QUERIES
  const { data, error, loading } = useQuery(GET_USER_EDIT);
  const history = useHistory();

  const [newUsername, setNewUsername] = useState(data && data.user.username);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [isChangingAvatar, setIsChangingAvatar] = useState(false);

  // Set current avatar when data loads
  useEffect(() => {
    if (data && data.user && data.user.avatarId) {
      setSelectedAvatar(data.user.avatarId);
    }
  }, [data]);

  //MUTATIONS
  const [changeName] = useMutation(EDIT_USER_NAME, {
    update(cache, addContentLinkResponse) {
      setNewUsername("");
      document.getElementById("editable_div_new_username").innerText = "";
    },
  });

  const [setUserAvatarMutation] = useMutation(EDIT_USER_PHOTO, {});

  const setUserAvatar = async (avatarId) => {
    if (isChangingAvatar || selectedAvatar === avatarId) return;
    
    setIsChangingAvatar(true);
    try {
      await setUserAvatarMutation({
        variables: {
          avatarId,
        },
      });
      setSelectedAvatar(avatarId);
      showSuccessToast(
        <FormattedMessage id={"edit-user-data-profile-updated-successfully"} />
      );
    } catch (err) {
      showErrorToast(
        <FormattedMessage id={"edit-user-data-profile-updated-failed"} />
      );
    } finally {
      setIsChangingAvatar(false);
    }
  };
 
  const avatars = [
    { id: 1, src: avatar1 },
    { id: 2, src: avatar2 },
    { id: 3, src: avatar3 },
    { id: 4, src: avatar4 },
    { id: 5, src: avatar5 },
    { id: 6, src: avatar6 },
    { id: 7, src: avatar7 },
    { id: 8, src: avatar8 },
    { id: 9, src: avatar9 },
  ];

  if (loading) {
    return (
      <div>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>
            <FormattedMessage id="loading" defaultMessage="Loading..." />
          </LoadingText>
        </LoadingContainer>
        <Header error={false} loading={true} props={props} />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ErrorContainer>
          <ErrorMessage>
            <FormattedMessage 
              id="error-loading-avatars" 
              defaultMessage="Error loading avatars. Please try again." 
            />
          </ErrorMessage>
        </ErrorContainer>
        <Header error={true} loading={false} props={props} />
      </div>
    );
  }

  return (
    <div>
      {data && (
        <Container>
          <EditProfilePhotoWrapper>
            <ProfilePhotoWithName hideName={true} hidePhoto={true} />
          </EditProfilePhotoWrapper>
          <ContentWrapper>
            <Title>
              <FormattedMessage 
                id="choose-avatar" 
                defaultMessage="Choose your avatar" 
              />
            </Title>
            <AvatarsWrapper>
              {avatars.map((avatar) => (
                <AvatarContainer key={avatar.id}>
                  <Avatar 
                    src={avatar.src} 
                    onClick={() => setUserAvatar(avatar.id)}
                    isSelected={selectedAvatar === avatar.id}
                    isChanging={isChangingAvatar}
                    disabled={isChangingAvatar}
                    tabIndex={0}
                    role="button"
                    aria-label={`Select avatar ${avatar.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setUserAvatar(avatar.id);
                      }
                    }}
                  />
                  {selectedAvatar === avatar.id && (
                    <SelectedIndicator>
                      <CheckIcon>✓</CheckIcon>
                    </SelectedIndicator>
                  )}
                  {isChangingAvatar && selectedAvatar === avatar.id && (
                    <LoadingOverlay>
                      <Spinner />
                    </LoadingOverlay>
                  )}
                </AvatarContainer>
              ))}
            </AvatarsWrapper>
            {isChangingAvatar && (
              <ChangingText>
                <FormattedMessage 
                  id="changing-avatar" 
                  defaultMessage="Changing avatar..." 
                />
              </ChangingText>
            )}
            <ButtonContainer>
              <ReturnButton onClick={() => history.push('/profile/edit')}>
                <FormattedMessage 
                  id="return-to-profile" 
                  defaultMessage="Return to Profile" 
                />
              </ReturnButton>
            </ButtonContainer>
          </ContentWrapper>
        </Container>
      )}
      <Header error={false} loading={false} props={props} />
    </div>
  );
};

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const bounce = keyframes`
  0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
  60% { transform: translateY(-5px); }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  margin-right: 15%;
  margin-top: 120px;
  ${css`animation: ${fadeIn} 0.6s ease-out;`}

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    flex-direction: column;
    margin-top: 100px;
    margin-right: 0%;
  }
`;

const EditProfilePhotoWrapper = styled.div`
  margin-left: 20px;
  min-width: 200px;

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    display: none;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 70px;
  padding: 0 20px;

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    margin-top: 0px;
    padding: 0 10px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => props.theme.textColor || '#333'};
  margin-bottom: 30px;
  text-align: center;
  ${css`animation: ${fadeIn} 0.8s ease-out;`}

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    font-size: 20px;
    margin-bottom: 20px;
  }
`;

const AvatarsWrapper = styled.div`
  width: 100%;
  max-width: 700px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 30px;
  justify-items: center;
  align-items: center;
  padding: 20px;

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    padding: 10px;
  }

  @media only screen and (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
`;

const AvatarContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  ${css`animation: ${fadeIn} 0.6s ease-out;`}

  &:hover {
    transform: translateY(-5px);
  }
`;

const Avatar = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid transparent;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  ${props => props.isSelected && css`
    border-color: ${props.theme.primaryColor || '#007bff'};
    box-shadow: 0 0 0 3px ${props.theme.primaryColor || '#007bff'}20;
    animation: ${pulse} 2s infinite;
  `}
  
  ${props => props.isChanging && css`
    opacity: 0.7;
    cursor: not-allowed;
  `}
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
  }
  
  &:focus {
    outline: 2px solid ${props => props.theme.primaryColor || '#007bff'};
    outline-offset: 2px;
  }

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    width: 80px;
    height: 80px;
  }

  @media only screen and (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

const SelectedIndicator = styled.div`
  position: absolute;
  top: -5px;
  right: -5px;
  width: 30px;
  height: 30px;
  background: ${props => props.theme.primaryColor || '#007bff'};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${css`animation: ${bounce} 0.6s ease-out;`}
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const CheckIcon = styled.span`
  color: white;
  font-size: 16px;
  font-weight: bold;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
`;

const Spinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.theme.primaryColor || '#007bff'};
  border-top: 2px solid transparent;
  border-radius: 50%;
  ${css`animation: ${spin} 1s linear infinite;`}
`;

const ChangingText = styled.div`
  margin-top: 20px;
  color: ${props => props.theme.textColor || '#666'};
  font-size: 14px;
  ${css`animation: ${fadeIn} 0.3s ease-out;`}
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${props => props.theme.primaryColor || '#007bff'}20;
  border-top: 4px solid ${props => props.theme.primaryColor || '#007bff'};
  border-radius: 50%;
  ${css`animation: ${spin} 1s linear infinite;`}
`;

const LoadingText = styled.div`
  color: ${props => props.theme.textColor || '#666'};
  font-size: 16px;
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 20px;
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.errorColor || '#dc3545'};
  font-size: 16px;
  text-align: center;
  background: ${props => props.theme.errorColor || '#dc3545'}10;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.errorColor || '#dc3545'}30;
`;

const ButtonContainer = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ReturnButton = styled.button`
  background: ${props => props.theme.primaryColor || '#007bff'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  ${css`animation: ${fadeIn} 0.6s ease-out;`}
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    background: ${props => props.theme.primaryColorDark || '#0056b3'};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: 2px solid ${props => props.theme.primaryColor || '#007bff'};
    outline-offset: 2px;
  }

  @media only screen and (max-width: ${(props) =>
      props.theme.secondBreakpoint}px) {
    padding: 10px 20px;
    font-size: 14px;
  }
`;
